import React, { useMemo } from 'react';
import { useImpersonation } from '../context/ImpersonationContext';
import { getFeatureStatus } from '../config/featureStatus';
import { ALL_NAV_ITEMS, MOBILE_PRIORITY, MAX_PRIMARY_TABS } from '../config/navItems';
import { isItemVisible } from '../config/permissions';

// Same visibility resolver as Sidebar — keeps mobile and desktop in sync.
function resolveItemVisibility(itemKey, isImpersonating, qaShowHidden) {
  if (!isImpersonating) return 'normal';
  const status = getFeatureStatus(itemKey);
  if (status === 'live') return 'normal';
  if (status === 'coming-soon') return 'coming-soon';
  if (status === 'hidden') return qaShowHidden ? 'hidden-revealed' : null;
  return 'normal';
}

// Red count bubble for the Task Calendar tab (uncompleted tasks due today).
function TaskCalBadge({ count, top = -2, right = 6 }) {
  if (!count) return null;
  return (
    <span style={{
      position: 'absolute', top, right, minWidth: 16, height: 16,
      borderRadius: 8, background: '#e5484d', color: '#fff', fontSize: 10,
      fontWeight: 700, lineHeight: '16px', padding: '0 4px', textAlign: 'center',
    }}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function MobileNav({ activeView, onNavigate, hasModule }) {
  const [showMore, setShowMore] = React.useState(false);
  const { isImpersonating, impersonatedSession, qaShowHidden } = useImpersonation();

  // Same badge as the Sidebar: my pending tasks due today, refreshed by the
  // taskcal-badge-refresh event TaskCalendar dispatches after mutations.
  const [taskCalCount, setTaskCalCount] = React.useState(0);
  React.useEffect(() => {
    let opsToken = null;
    try { opsToken = JSON.parse(localStorage.getItem('staff-session'))?.opsToken; } catch {}
    if (!opsToken) return undefined;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ops/task-calendar/badge-count`, {
          headers: { 'Authorization': `Bearer ${opsToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTaskCalCount(data.count || 0);
        }
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 2 * 60 * 1000);
    window.addEventListener('taskcal-badge-refresh', fetchCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('taskcal-badge-refresh', fetchCount);
    };
  }, []);

  // When impersonating, use the synthetic session for the target user so
  // visibility matches their full picture (role + admin overlay + toggles).
  const session = useMemo(() => {
    if (isImpersonating) return impersonatedSession;
    try { return JSON.parse(localStorage.getItem('staff-session')); } catch { return null; }
  }, [isImpersonating, impersonatedSession]);

  // Filter the same item set the Sidebar uses, by role + feature status.
  const visibleItems = useMemo(() => {
    return ALL_NAV_ITEMS
      .filter(i => isItemVisible(i, session))
      .map(item => ({ item, vis: resolveItemVisibility(item.key, isImpersonating, qaShowHidden) }))
      .filter(x => x.vis !== null);
  }, [hasModule, session, isImpersonating, qaShowHidden]);

  // Pick primaries by MOBILE_PRIORITY order (first MAX_PRIMARY_TABS visible
  // items that appear in the priority list). Anything else → More.
  const { primaries, more } = useMemo(() => {
    const byKey = new Map(visibleItems.map(x => [x.item.key, x]));
    const orderedPrimary = [];
    for (const key of MOBILE_PRIORITY) {
      if (orderedPrimary.length >= MAX_PRIMARY_TABS) break;
      const hit = byKey.get(key);
      if (hit) {
        orderedPrimary.push(hit);
        byKey.delete(key);
      }
    }
    // Anything still in byKey is overflow. Items not in priority list at all
    // also go to More, in their original order.
    const moreItems = visibleItems.filter(x => byKey.has(x.item.key) || !MOBILE_PRIORITY.includes(x.item.key));
    // De-dupe in case the inclusion test above double-counts.
    const seen = new Set();
    const moreUnique = moreItems.filter(x => {
      if (seen.has(x.item.key)) return false;
      seen.add(x.item.key);
      return !orderedPrimary.some(p => p.item.key === x.item.key);
    });
    return { primaries: orderedPrimary, more: moreUnique };
  }, [visibleItems]);

  const isInMore = more.some(x => x.item.key === activeView);

  return (
    <>
      {/* More sheet */}
      {showMore && more.length > 0 && (
        <>
          <div
            onClick={() => setShowMore(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
              zIndex: 50, animation: 'fade-in 100ms ease',
            }}
          />
          <div style={{
            position: 'fixed', bottom: 56, left: 0, right: 0,
            background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)', zIndex: 51,
            padding: '12px 8px', maxHeight: '60vh', overflowY: 'auto',
            animation: 'slide-up 200ms ease',
          }}>
            {more.map(({ item, vis }) => {
              const isComingSoon = vis === 'coming-soon';
              const isHiddenRevealed = vis === 'hidden-revealed';
              const clickable = vis === 'normal' || isHiddenRevealed || (isComingSoon && qaShowHidden);
              return (
                <button
                  key={item.key}
                  onClick={() => { if (clickable) { onNavigate(item.key); setShowMore(false); } }}
                  disabled={!clickable}
                  title={isComingSoon ? (qaShowHidden ? 'Coming soon (QA: clickable)' : 'Coming soon') : isHiddenRevealed ? 'Hidden from staff (QA only)' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', padding: '12px 16px',
                    border: isHiddenRevealed ? '1px dashed #b5556a' : 'none',
                    background: activeView === item.key ? '#FDF2F7' : 'transparent',
                    borderRadius: 8,
                    cursor: clickable ? 'pointer' : 'not-allowed',
                    fontSize: 15, fontWeight: 500,
                    color: activeView === item.key ? '#F07EA0' : isComingSoon && !qaShowHidden ? '#9CA3AF' : '#111827',
                    opacity: isComingSoon && !qaShowHidden ? 0.6 : 1,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <span style={{ fontSize: 18, position: 'relative' }}>
                    {item.icon}
                    {item.key === 'task_calendar' && <TaskCalBadge count={taskCalCount} top={-4} right={-10} />}
                  </span>
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                  {isComingSoon && (
                    <span style={{
                      background: '#FEF3C7', color: '#92400E', fontSize: 9, fontWeight: 700,
                      padding: '1px 6px', borderRadius: 9999, textTransform: 'uppercase',
                    }}>Soon</span>
                  )}
                  {isHiddenRevealed && (
                    <span style={{
                      background: '#fdf0f4', color: '#b5556a', fontSize: 12, fontWeight: 600,
                      padding: '1px 6px', borderRadius: 9999,
                    }}>🚧</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Bottom tab bar */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, height: 56,
        background: '#fff', borderTop: '1px solid #E5E7EB',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        zIndex: 40, paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {primaries.map(({ item, vis }) => {
          const isComingSoon = vis === 'coming-soon';
          const isHiddenRevealed = vis === 'hidden-revealed';
          const clickable = vis === 'normal' || isHiddenRevealed || (isComingSoon && qaShowHidden);
          const active = activeView === item.key;
          return (
            <button
              key={item.key}
              onClick={() => { if (clickable) onNavigate(item.key); }}
              disabled={!clickable}
              title={isComingSoon ? (qaShowHidden ? 'Coming soon (QA: clickable)' : 'Coming soon') : isHiddenRevealed ? 'Hidden from staff (QA only)' : undefined}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 2, border: 'none', background: 'none',
                cursor: clickable ? 'pointer' : 'not-allowed',
                padding: '4px 0', minWidth: 56, position: 'relative',
                color: active ? '#F07EA0' : isComingSoon && !qaShowHidden ? '#D1D5DB' : '#9CA3AF',
                opacity: isComingSoon && !qaShowHidden ? 0.6 : 1,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <span style={{ fontSize: 20 }}>{item.icon}</span>
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 500, maxWidth: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.label}
              </span>
              {item.key === 'task_calendar' && <TaskCalBadge count={taskCalCount} />}
              {(isComingSoon || isHiddenRevealed) && (
                <span style={{
                  position: 'absolute', top: 2, right: 8,
                  background: isHiddenRevealed ? '#fdf0f4' : '#FEF3C7',
                  color: isHiddenRevealed ? '#b5556a' : '#92400E',
                  fontSize: 9, lineHeight: 1, padding: '1px 4px', borderRadius: 9999,
                  fontWeight: 700,
                }}>
                  {isHiddenRevealed ? '🚧' : 'soon'}
                </span>
              )}
            </button>
          );
        })}

        {more.length > 0 && (
          <button
            key="_more"
            onClick={() => setShowMore(s => !s)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 2, border: 'none', background: 'none', cursor: 'pointer',
              padding: '4px 0', minWidth: 56,
              color: isInMore || showMore ? '#F07EA0' : '#9CA3AF',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            <span style={{ fontSize: 20 }}>⋯</span>
            <span style={{ fontSize: 10, fontWeight: isInMore || showMore ? 600 : 500 }}>More</span>
          </button>
        )}
      </nav>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
